import { Button, Stack, Typography, useMediaQuery, useTheme } from '@mui/material'
import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react'
import { PageIndex, setPageIndex, setPageName } from '../../store/redux/reducers/pageNameSlice'
import { t } from 'i18next'
import { useAppDispatch, useEditMode, useNotification } from '../../store/redux/hooks'
import {
  useCreateRecruitmentMutation,
  useEditRecruitmentMutation,
  useLazyGetRecruitmentByIdQuery,
} from '../../store/redux/reducers/recruitmentSlice'
import { Controller, useForm } from 'react-hook-form'
import { defaultRecruitmentFormValues, RecruitmentFormValues } from '../../store/types/form/RecruitmentFormValues'
import { Add, Check, Clear } from '@mui/icons-material'
import { useParams } from 'react-router-dom'
import { createErrorMessage } from '../../store/utils'
import DateFormat from '../../store/types/enums/DateFormat'
import DateInput from '../../components/Form/Inputs/DateInput'
import dayjs, { Dayjs } from 'dayjs'
import AutocompleteInput from '../../components/Form/Inputs/AutocompleteInput'
import { breadcrumbsItemsActions } from '../../store/redux/reducers/breadcrumbsSlice'
import SelectInput from '../../components/Form/Inputs/SelectInput'
import TextInput from '../../components/Form/Inputs/TextInput'
import DiscardChangesDialog from '../../components/Dialog/DiscardChangesDialog'
import sortByField from '../../store/utils/sortByField'
import { LoadingButton } from '@mui/lab'
import { useRecruitmentWorkflow } from '../../store/utils/useRecruitmentWorkflow'
import { TimePicker } from '@mui/x-date-pickers'
import { Box } from '@mui/system'
import Recruitment from '../../store/types/models/Recruitment'
import DecisionEnum from '../../store/types/enums/DecisionEnum'
import { useGetDictionariesQuery } from '../../store/redux/reducers/dictionarySlice'

type AddRecruitmentViewProps = {
  setIsRecruitmentForm: Dispatch<SetStateAction<boolean>>
  editedRecruitmentId: string | undefined
  latestRecruitment?: Recruitment
}

const AddRecruitment = ({ setIsRecruitmentForm, editedRecruitmentId, latestRecruitment }: AddRecruitmentViewProps) => {
  const { displayNotification } = useNotification()
  const dispatch = useAppDispatch()
  const theme = useTheme()
  const isDownSm = useMediaQuery(theme.breakpoints.down('sm'))
  const [createRecruitment] = useCreateRecruitmentMutation()
  const [editRecruitment] = useEditRecruitmentMutation()
  const [getRecruitmentById] = useLazyGetRecruitmentByIdQuery()
  const { setIsEditMode } = useEditMode()
  const { id: candidateId } = useParams()
  const isEdit = editedRecruitmentId !== undefined
  const [candidateName, setCandidateName] = useState<string>()

  const { handleSubmit, control, reset, formState, watch, setValue } = useForm<RecruitmentFormValues>({
    defaultValues: async () => {
      if (editedRecruitmentId !== undefined) {
        const result = await getRecruitmentById(editedRecruitmentId)

        if (result.isSuccess) {
          const recruitment = result.data
          setCandidateName(`${recruitment.candidate.firstName} ${recruitment.candidate.lastName}`)

          return {
            ...recruitment,
            candidateId: candidateId || '',
            responsibles: sortByField(
              recruitment.responsibles.map(responsible => ({
                value: responsible.id,
                label: `${responsible.firstName} ${responsible.lastName}`,
              })),
              'label'
            ),
            completionDate: dayjs(recruitment.completionDate).utc(true),
            completionTime: dayjs(recruitment.completionDate).utc(true),
            step: recruitment.step.id,
            stage: recruitment.stage.id,
          }
        } else if (result.error !== undefined && 'status' in result?.error && result.error.status === 404) {
          displayNotification({ message: `${t('page.users.message.error')}`, type: 'error' })
        }
      }
      return defaultRecruitmentFormValues
    },
  })

  const watchedStage = watch('stage')
  const watchedDecision = watch('decision')
  const { managementAndRecruiters, stageOptions, stepOptions, decisionOptions } = useRecruitmentWorkflow(watchedStage)

  const isEditingLastStep = useMemo(() => {
    return latestRecruitment?.id === editedRecruitmentId
  }, [latestRecruitment, editedRecruitmentId])
  const isLastStepRejected = useMemo(() => {
    return latestRecruitment?.decision === DecisionEnum.REJECTED
  }, [latestRecruitment])
  const isRejected = useMemo(() => {
    return isLastStepRejected && !isEditingLastStep
  }, [isLastStepRejected, isEditingLastStep])

  const { data: archiveStageResponse } = useGetDictionariesQuery(
    {
      page: 0,
      size: 1,
      group: 'RECRUITMENT_WORKFLOW',
      key: 'RECRUITMENT_ARCHIVE',
      sortField: 'order',
    },
    { skip: !isRejected }
  )

  useEffect(() => {
    if (isRejected && watchedDecision !== DecisionEnum.REJECTED && !editedRecruitmentId) {
      setValue('decision', DecisionEnum.REJECTED)
    }
  }, [isRejected, setValue, watchedDecision, editedRecruitmentId])

  useEffect(() => {
    const archiveStageId = archiveStageResponse?.content[0].id
    if (isRejected && archiveStageId && watchedStage !== archiveStageId && !editedRecruitmentId) {
      setValue('stage', archiveStageId)
    }
  }, [archiveStageResponse?.content, isRejected, setValue, watchedStage, editedRecruitmentId])

  useEffect(() => {
    dispatch(setPageName(isEdit ? t('page.name.candidates.editRecruitment') : t('page.name.candidates.newRecruitment')))
    dispatch(setPageIndex(PageIndex.CANDIDATES))
    if (candidateName !== undefined) {
      dispatch(breadcrumbsItemsActions.setInitialState())
      dispatch(
        breadcrumbsItemsActions.setSelectedItems({
          candidates: candidateName,
        })
      )
    }
  })

  useEffect(() => {
    setIsEditMode(true)
  })

  const { isSubmitting } = formState

  const getMergedCompletionDate = (completionDate: Dayjs | undefined, completionTime: Dayjs | undefined) => {
    return completionDate && completionTime
      ? completionDate.set('hour', completionTime.hour()).set('minute', completionTime.minute()).set('second', 0)
      : undefined
  }

  const handleCreate = async (data: RecruitmentFormValues) => {
    const dataWithCandidateId = {
      ...data,
      candidateId: candidateId,
      completionDate: getMergedCompletionDate(data.completionDate, data?.completionTime)?.format(
        DateFormat.IsoLocalDateTime
      ),
      responsibles: data.responsibles?.map(responsible => responsible.value),
    }

    try {
      await createRecruitment(dataWithCandidateId).unwrap()
      displayNotification({ message: t('page.addRecruitment.alert.recruitmentCreated'), type: 'success' })
      setIsEditMode(false)
      reset(data)
      setTimeout(() => setIsRecruitmentForm(false), 0)
    } catch (error) {
      let errorDescription = createErrorMessage(error)
      displayNotification({
        message: `${t('page.addRecruitment.alert.recruitmentCreationError')} ${errorDescription}`,
        type: 'error',
      })
    }
  }

  const handleEdit = async (data: RecruitmentFormValues) => {
    if (editedRecruitmentId !== undefined) {
      const dataWithFormattedDate = {
        ...data,
        completionDate: getMergedCompletionDate(data.completionDate, data?.completionTime)?.format(
          DateFormat.IsoLocalDateTime
        ),
        responsibles: data.responsibles?.map(responsible => responsible.value),
      }

      try {
        await editRecruitment({ id: editedRecruitmentId, recruitment: dataWithFormattedDate }).unwrap()
        reset(data)
        displayNotification({ message: t('page.addRecruitment.alert.recruitmentEdited'), type: 'success' })
        setIsEditMode(false)
        setTimeout(() => setIsRecruitmentForm(false), 0)
      } catch (error) {
        let errorDescription = createErrorMessage(error)
        displayNotification({
          message: `${t('page.addRecruitment.alert.recruitmentEditionError')} ${errorDescription}`,
          type: 'error',
        })
      }
    }
  }

  const handleSubmitEdit = () => {
    handleSubmit(data => handleEdit(data))()
  }

  const handleSubmitCreate = () => {
    handleSubmit(data => handleCreate(data))()
  }

  const { isDirty } = formState

  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false)
  const checkUnsavedChanges = () => {
    return isDirty
  }

  return (
    <Stack sx={{ px: 2, gap: 2, pr: isDownSm ? 0 : 2 }}>
      <Stack direction="row" justifyContent="space-between">
        <Typography variant={'caption'} sx={{ alignContent: 'center', color: 'warning.main' }}>
          {isLastStepRejected ? t('page.addRecruitment.errors.rejectedCandidateError') : ''}
        </Typography>
        <Stack direction={'row'} sx={{ justifyContent: 'end', gap: 2 }}>
          <Button
            variant={'contained'}
            color={'secondary'}
            startIcon={<Clear />}
            disabled={isSubmitting}
            onClick={() => setIsRecruitmentForm(false)}
          >
            {t('page.usersProfile.header.cancel')}
          </Button>
          <LoadingButton
            variant="contained"
            startIcon={isEdit ? <Check /> : <Add />}
            size="medium"
            onClick={() => (isEdit ? handleSubmitEdit() : handleSubmitCreate())}
            fullWidth={isDownSm}
            disabled={isSubmitting}
            loading={isSubmitting}
          >
            {t('components.button.saveRecruitment')}
          </LoadingButton>
        </Stack>
      </Stack>
      <Stack direction={isDownSm ? 'column' : 'row'} sx={{ gap: 2 }}>
        <Stack sx={{ gap: 3, width: '100%' }}>
          <SelectInput
            disabled={isRejected || (isEditingLastStep && isLastStepRejected)}
            options={stageOptions}
            name={'stage'}
            control={control}
            label={t('page.addRecruitment.stage') + ' *'}
            rules={{ required: t('page.addRecruitment.errors.stageRequired') }}
          />

          <SelectInput
            disabled={isLastStepRejected && editedRecruitmentId !== undefined}
            options={stepOptions}
            name={'step'}
            control={control}
            label={t('page.addRecruitment.step') + ' *'}
            rules={{ required: t('page.addRecruitment.errors.stepRequired') }}
          />

          <AutocompleteInput
            disabled={isEditingLastStep && isLastStepRejected}
            options={
              managementAndRecruiters
                ? managementAndRecruiters.content.map(user => ({
                    value: user.id,
                    label: `${user.firstName} ${user.lastName}`,
                  }))
                : []
            }
            multiple
            disableCloseOnSelect
            size={'small'}
            name={'responsibles'}
            control={control}
            label={t('page.candidateDetails.tiles.responsibles')}
            sortValues={values => sortByField(values, 'label')}
          />
          <SelectInput
            disabled={isRejected}
            options={decisionOptions}
            name={'decision'}
            control={control}
            label={t('page.addRecruitment.decision') + ' *'}
            rules={{ required: t('page.addRecruitment.errors.decisionRequired') }}
          />
        </Stack>
        <Stack sx={{ gap: 3, width: '100%' }}>
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
            <DateInput
              control={control}
              name={'completionDate'}
              label={t('page.addRecruitment.completionDate') + ' *'}
              defaultValue={dayjs().utc(true)}
              rules={{ required: t('page.addRecruitment.errors.completionDateRequired') }}
              flex1
            />
            <Controller
              name={'completionTime'}
              control={control}
              rules={{ required: t('page.addRecruitment.errors.completionTimeRequired') }}
              render={({ field: { value, onChange, name }, fieldState }) => (
                <TimePicker
                  value={value || null}
                  label={t('page.addRecruitment.completionTime') + ' *'}
                  onChange={onChange}
                  ampm={false}
                  slotProps={{
                    textField: {
                      size: 'small',
                      helperText: fieldState.error?.message,
                      error: !!fieldState.error || !dayjs(value, DateFormat.HourMinute, true).isValid(),
                      name: name,
                      fullWidth: true,
                    },
                  }}
                  sx={{ flex: 1 }}
                />
              )}
            />
          </Box>
          <TextInput
            name={'comments'}
            control={control}
            label={t('page.addRecruitment.comments')}
            maxLength={500}
            rules={{
              maxLength: { value: 500, message: t('page.worklog.form.descriptionLengthError') },
            }}
            multiline
            rows={7}
            showCharCounter
          />
        </Stack>
      </Stack>
      <DiscardChangesDialog
        open={isCancelDialogOpen}
        checkUnsavedChanges={checkUnsavedChanges}
        setIsCancelDialogOpen={setIsCancelDialogOpen}
      />
    </Stack>
  )
}

export default AddRecruitment